import React, { useEffect, useRef, useState } from 'react';

// Type definitions for Google Maps Places API
interface GoogleMapsPlacesAutocomplete {
  addListener: (event: string, callback: () => void) => void;
  getPlace: () => any; // PlaceResult from Google Maps
}

interface GoogleMapsPlaces {
  Autocomplete: new (input: HTMLInputElement, options?: { types?: string[]; fields?: string[] }) => GoogleMapsPlacesAutocomplete;
  PlacesServiceStatus: {
    OK: string;
  };
}

interface GoogleMaps {
  maps: {
    places: GoogleMapsPlaces;
    event?: {
      clearInstanceListeners?: (instance: any) => void;
    };
  };
}

// Helper to safely access Google Maps API
const getGoogleMaps = (): GoogleMaps | undefined => {
  return (window as any).google as GoogleMaps | undefined;
}

export interface LocationData {
  name: string; // Place name (e.g., "Irvington Tennis Club")
  formatted_address: string; // Full formatted address
  latitude: number;
  longitude: number;
}

interface LocationAutocompleteProps {
  value: string;
  onChange: (location: LocationData | null) => void;
  onLocationNameChange?: (name: string) => void; // For backward compatibility
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  onLocationNameChange,
  placeholder = 'Enter location',
  className = '',
  id,
  name = 'location',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<GoogleMapsPlacesAutocomplete | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      const google = getGoogleMaps();
      if (google && google.maps && google.maps.places) {
        setMapsLoaded(true);
      } else {
        // Retry after a short delay if not loaded yet
        setTimeout(checkGoogleMaps, 100);
      }
    };

    checkGoogleMaps();
    // Also check periodically in case script loads later
    const interval = setInterval(checkGoogleMaps, 500);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      // After 5 seconds, give up and use plain text input
      if (!mapsLoaded) {
        console.warn('Google Maps API not loaded, using plain text input');
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [mapsLoaded]);

  // Initialize autocomplete when maps is loaded
  useEffect(() => {
    if (!mapsLoaded || !inputRef.current) {
      return;
    }

    try {
      // Initialize autocomplete
      const google = getGoogleMaps();
      if (!google?.maps?.places || !inputRef.current) {
        console.error('Google Maps Places API not loaded');
        return;
      }
      
      autocompleteRef.current = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['establishment', 'geocode'], // Include businesses and addresses
          fields: ['name', 'formatted_address', 'geometry'],
        }
      ) as GoogleMapsPlacesAutocomplete;

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();

        if (place && place.geometry && place.geometry.location) {
          const locationData: LocationData = {
            name: place.name || inputRef.current?.value || '',
            formatted_address: place.formatted_address || '',
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          };

          // Update input value to show the place name
          setInputValue(locationData.name);
          
          // Call onChange with location data
          onChange(locationData);
          
          // Also call onLocationNameChange for backward compatibility
          if (onLocationNameChange) {
            onLocationNameChange(locationData.name);
          }
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setMapsLoaded(false); // Fallback to plain text
    }

    return () => {
      if (autocompleteRef.current) {
        // Cleanup if needed (Google Maps doesn't expose a destroy method, but we can clear listeners)
        const google = getGoogleMaps();
        google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      }
    };
  }, [mapsLoaded, onChange, onLocationNameChange]);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // If user manually types and clears, clear the location data
    if (!newValue) {
      onChange(null);
    }

    // Call onLocationNameChange for backward compatibility
    if (onLocationNameChange) {
      onLocationNameChange(newValue);
    }
  };

  // Default input classes matching the existing form inputs
  const defaultClasses = 'mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 min-h-[44px]';

  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      className={`${defaultClasses} ${className}`}
      autoComplete="off" // Disable browser autocomplete to use Google Places
    />
  );
};

export default LocationAutocomplete;

